// TaxonomyEditor · Pack 8.5 admin UI
// Stewards can add/edit countries, industries, functions and tune cascade
// weights without a code redeploy. All writes go through /api/taxonomy/admin/*
// which stamps GLOBAL_TENANT via TenantContext.system() on the backend.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Plus, Save, Trash2, RefreshCw, SlidersHorizontal, Scale } from "lucide-react";
import BenchmarkScopeEditor from "./BenchmarkScopeEditor";

const empty = { country: { id: "", name: "", region: "", sub_region: "", currency: "", languages: "" },
                industry: { id: "", name: "", aliases: "" },
                fn: { id: "", name: "", kpi_groups: "" } };

export default function TaxonomyEditor() {
  const [countries, setCountries] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(empty);
  // Weight tuner state
  const [tunerCountry, setTunerCountry] = useState("");
  const [tunerIndustry, setTunerIndustry] = useState("");
  const [ciEdges, setCiEdges] = useState([]);
  const [ifEdges, setIfEdges] = useState([]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, i, f] = await Promise.all([
        api.get("/taxonomy/countries?limit=500"),
        api.get("/taxonomy/industries"),
        api.get("/taxonomy/functions"),
      ]);
      setCountries(c.data.countries || []);
      setIndustries(i.data.industries || []);
      setFunctions(f.data.functions || []);
    } catch {
      toast.error("Failed to load taxonomy");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ---------- Countries -----------------------------------------------------
  const saveCountry = async () => {
    const d = draft.country;
    if (!d.id || !d.name || !d.region || !d.currency) {
      return toast.error("id, name, region, currency are required");
    }
    try {
      await api.post("/taxonomy/admin/countries", {
        id: d.id.trim().toUpperCase(),
        name: d.name.trim(),
        region: d.region.trim(),
        sub_region: d.sub_region.trim(),
        currency: d.currency.trim().toUpperCase(),
        languages: d.languages.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success(`${d.id} saved`);
      setDraft({ ...draft, country: empty.country });
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };
  const deleteCountry = async (id) => {
    if (!window.confirm(`Delete country ${id} and cascade all edges?`)) return;
    try {
      const r = await api.delete(`/taxonomy/admin/countries/${encodeURIComponent(id)}`);
      toast.success(`${id} removed (${r.data.removed} rows)`);
      loadAll();
    } catch {
      toast.error("Delete failed");
    }
  };

  // ---------- Industries ----------------------------------------------------
  const saveIndustry = async () => {
    const d = draft.industry;
    if (!d.id || !d.name) return toast.error("id and name are required");
    try {
      await api.post("/taxonomy/admin/industries", {
        id: d.id.trim(),
        name: d.name.trim(),
        aliases: d.aliases.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success(`${d.id} saved`);
      setDraft({ ...draft, industry: empty.industry });
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };
  const deleteIndustry = async (id) => {
    if (!window.confirm(`Delete industry ${id} and cascade all edges?`)) return;
    try {
      const r = await api.delete(`/taxonomy/admin/industries/${encodeURIComponent(id)}`);
      toast.success(`${id} removed (${r.data.removed} rows)`);
      loadAll();
    } catch { toast.error("Delete failed"); }
  };

  // ---------- Functions -----------------------------------------------------
  const saveFunction = async () => {
    const d = draft.fn;
    if (!d.id || !d.name) return toast.error("id and name are required");
    try {
      await api.post("/taxonomy/admin/functions", {
        id: d.id.trim().toUpperCase(),
        name: d.name.trim(),
        kpi_groups: d.kpi_groups.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success(`${d.id} saved`);
      setDraft({ ...draft, fn: empty.fn });
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };
  const deleteFunction = async (id) => {
    if (!window.confirm(`Delete function ${id} and cascade all edges?`)) return;
    try {
      const r = await api.delete(`/taxonomy/admin/functions/${encodeURIComponent(id)}`);
      toast.success(`${id} removed (${r.data.removed} rows)`);
      loadAll();
    } catch { toast.error("Delete failed"); }
  };

  // ---------- Weight tuner --------------------------------------------------
  useEffect(() => {
    if (!tunerCountry) { setCiEdges([]); return; }
    api.get(`/taxonomy/admin/edges/country-industry?country_id=${encodeURIComponent(tunerCountry)}`)
      .then((r) => setCiEdges(r.data.edges || []))
      .catch(() => setCiEdges([]));
  }, [tunerCountry]);

  useEffect(() => {
    if (!tunerIndustry) { setIfEdges([]); return; }
    api.get(`/taxonomy/admin/edges/industry-function?industry_id=${encodeURIComponent(tunerIndustry)}`)
      .then((r) => setIfEdges(r.data.edges || []))
      .catch(() => setIfEdges([]));
  }, [tunerIndustry]);

  const industriesById = useMemo(() => Object.fromEntries(industries.map((i) => [i.id, i])), [industries]);
  const functionsById = useMemo(() => Object.fromEntries(functions.map((f) => [f.id, f])), [functions]);

  const saveCiWeight = async (industry_id, weight) => {
    try {
      await api.post("/taxonomy/admin/edges/country-industry", {
        country_id: tunerCountry, industry_id, weight: Number(weight),
      });
      setCiEdges((es) => es.map((e) => e.industry_id === industry_id ? { ...e, weight: Number(weight) } : e));
    } catch { toast.error("Weight save failed"); }
  };
  const saveIfWeight = async (function_id, weight) => {
    try {
      await api.post("/taxonomy/admin/edges/industry-function", {
        industry_id: tunerIndustry, function_id, weight: Number(weight),
      });
      setIfEdges((es) => es.map((e) => e.function_id === function_id ? { ...e, weight: Number(weight) } : e));
    } catch { toast.error("Weight save failed"); }
  };

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground" data-testid="taxonomy-loading">Loading taxonomy…</div>;
  }

  return (
    <div className="space-y-6" data-testid="taxonomy-editor">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl">Knowledge Taxonomy</h2>
          <p className="text-sm text-muted-foreground">
            Country / Industry / Function catalog + cascade weights.
            All rows live under <code>__GLOBAL__</code>. Changes are live immediately.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadAll} data-testid="taxonomy-refresh">
          <RefreshCw className="w-3.5 h-3.5 mr-2" /> Refresh
        </Button>
      </div>

      <Tabs defaultValue="countries">
        <TabsList>
          <TabsTrigger value="countries" data-testid="tax-tab-countries">Countries ({countries.length})</TabsTrigger>
          <TabsTrigger value="industries" data-testid="tax-tab-industries">Industries ({industries.length})</TabsTrigger>
          <TabsTrigger value="functions" data-testid="tax-tab-functions">Functions ({functions.length})</TabsTrigger>
          <TabsTrigger value="weights" data-testid="tax-tab-weights">
            <SlidersHorizontal className="w-3.5 h-3.5 mr-1.5" /> Cascade Weights
          </TabsTrigger>
          <TabsTrigger value="benchmarks" data-testid="tax-tab-benchmarks">
            <Scale className="w-3.5 h-3.5 mr-1.5" /> Benchmarks
          </TabsTrigger>
        </TabsList>

        {/* ------ Countries ------ */}
        <TabsContent value="countries" className="mt-4 space-y-4">
          <Card className="p-4">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Add / edit country</div>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
              <div><Label>ISO3</Label><Input data-testid="country-id" value={draft.country.id} onChange={(e) => setDraft({ ...draft, country: { ...draft.country, id: e.target.value } })} placeholder="SAU" /></div>
              <div className="md:col-span-2"><Label>Name</Label><Input data-testid="country-name" value={draft.country.name} onChange={(e) => setDraft({ ...draft, country: { ...draft.country, name: e.target.value } })} placeholder="Saudi Arabia" /></div>
              <div><Label>Region</Label><Input value={draft.country.region} onChange={(e) => setDraft({ ...draft, country: { ...draft.country, region: e.target.value } })} placeholder="MENA" /></div>
              <div><Label>Currency</Label><Input value={draft.country.currency} onChange={(e) => setDraft({ ...draft, country: { ...draft.country, currency: e.target.value } })} placeholder="SAR" /></div>
              <div><Label>Languages (csv)</Label><Input value={draft.country.languages} onChange={(e) => setDraft({ ...draft, country: { ...draft.country, languages: e.target.value } })} placeholder="ar,en" /></div>
              <div className="md:col-span-6 flex gap-2">
                <Input placeholder="Sub-region (optional)" value={draft.country.sub_region} onChange={(e) => setDraft({ ...draft, country: { ...draft.country, sub_region: e.target.value } })} />
                <Button onClick={saveCountry} data-testid="country-save"><Plus className="w-4 h-4 mr-1.5" /> Save</Button>
              </div>
            </div>
          </Card>

          <Card className="p-0 overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead>ISO3</TableHead><TableHead>Name</TableHead><TableHead>Region</TableHead>
                <TableHead>Currency</TableHead><TableHead>Languages</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {countries.map((c) => (
                  <TableRow key={c.id} data-testid={`country-row-${c.id}`}>
                    <TableCell className="font-mono">{c.id}</TableCell>
                    <TableCell>{c.name}</TableCell>
                    <TableCell>{c.region}{c.sub_region ? ` · ${c.sub_region}` : ""}</TableCell>
                    <TableCell className="font-mono">{c.currency}</TableCell>
                    <TableCell className="text-xs">{(c.languages || []).join(", ")}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => setDraft({ ...draft, country: { ...c, languages: (c.languages || []).join(",") } })}><Save className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteCountry(c.id)} data-testid={`country-del-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* ------ Industries ------ */}
        <TabsContent value="industries" className="mt-4 space-y-4">
          <Card className="p-4">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Add / edit industry</div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
              <div><Label>ID</Label><Input data-testid="industry-id" value={draft.industry.id} onChange={(e) => setDraft({ ...draft, industry: { ...draft.industry, id: e.target.value } })} placeholder="NAICS_44-45" /></div>
              <div className="md:col-span-2"><Label>Name</Label><Input data-testid="industry-name" value={draft.industry.name} onChange={(e) => setDraft({ ...draft, industry: { ...draft.industry, name: e.target.value } })} placeholder="Retail Trade" /></div>
              <div className="md:col-span-4"><Label>Aliases (csv — first is the legacy intake label)</Label><Input value={draft.industry.aliases} onChange={(e) => setDraft({ ...draft, industry: { ...draft.industry, aliases: e.target.value } })} placeholder="Retail & E-commerce, Retail" /></div>
              <div className="md:col-span-4 flex justify-end">
                <Button onClick={saveIndustry} data-testid="industry-save"><Plus className="w-4 h-4 mr-1.5" /> Save</Button>
              </div>
            </div>
          </Card>

          <Card className="p-0 overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead>ID</TableHead><TableHead>Name</TableHead><TableHead>Aliases</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {industries.map((c) => (
                  <TableRow key={c.id} data-testid={`industry-row-${c.id}`}>
                    <TableCell className="font-mono text-xs">{c.id}</TableCell>
                    <TableCell>{c.name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{(c.aliases || []).join(", ")}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => setDraft({ ...draft, industry: { ...c, aliases: (c.aliases || []).join(",") } })}><Save className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteIndustry(c.id)} data-testid={`industry-del-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* ------ Functions ------ */}
        <TabsContent value="functions" className="mt-4 space-y-4">
          <Card className="p-4">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Add / edit function</div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
              <div><Label>ID</Label><Input data-testid="function-id" value={draft.fn.id} onChange={(e) => setDraft({ ...draft, fn: { ...draft.fn, id: e.target.value } })} placeholder="FIN" /></div>
              <div className="md:col-span-2"><Label>Name</Label><Input data-testid="function-name" value={draft.fn.name} onChange={(e) => setDraft({ ...draft, fn: { ...draft.fn, name: e.target.value } })} placeholder="Finance" /></div>
              <div className="md:col-span-4"><Label>KPI groups (csv)</Label><Input value={draft.fn.kpi_groups} onChange={(e) => setDraft({ ...draft, fn: { ...draft.fn, kpi_groups: e.target.value } })} placeholder="profitability, liquidity" /></div>
              <div className="md:col-span-4 flex justify-end">
                <Button onClick={saveFunction} data-testid="function-save"><Plus className="w-4 h-4 mr-1.5" /> Save</Button>
              </div>
            </div>
          </Card>

          <Card className="p-0 overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead>ID</TableHead><TableHead>Name</TableHead><TableHead>KPI Groups</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {functions.map((c) => (
                  <TableRow key={c.id} data-testid={`function-row-${c.id}`}>
                    <TableCell className="font-mono">{c.id}</TableCell>
                    <TableCell>{c.name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{(c.kpi_groups || []).join(", ")}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => setDraft({ ...draft, fn: { ...c, kpi_groups: (c.kpi_groups || []).join(",") } })}><Save className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteFunction(c.id)} data-testid={`function-del-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* ------ Weights ------ */}
        <TabsContent value="weights" className="mt-4 space-y-6">
          <Card className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Country → Industry weights</Label>
                <Select value={tunerCountry} onValueChange={setTunerCountry}>
                  <SelectTrigger data-testid="ci-country-picker" className="mt-1"><SelectValue placeholder="Pick a country to tune" /></SelectTrigger>
                  <SelectContent>{countries.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.id})</SelectItem>)}</SelectContent>
                </Select>
                {tunerCountry && (
                  <div className="mt-4 space-y-2 max-h-80 overflow-y-auto pr-2" data-testid="ci-edges">
                    {ciEdges.length === 0 && <p className="text-xs text-muted-foreground">No edges yet. Weights default to 1.0 in cascade queries.</p>}
                    {ciEdges.map((e) => (
                      <div key={e.industry_id} className="flex items-center gap-3 text-sm">
                        <span className="flex-1 truncate">{industriesById[e.industry_id]?.name || e.industry_id}</span>
                        <Input type="number" step="0.1" min="0" max="5" defaultValue={e.weight} onBlur={(ev) => saveCiWeight(e.industry_id, ev.target.value)} className="w-20 h-8" data-testid={`ci-w-${e.industry_id}`} />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <Label>Industry → Function weights</Label>
                <Select value={tunerIndustry} onValueChange={setTunerIndustry}>
                  <SelectTrigger data-testid="if-industry-picker" className="mt-1"><SelectValue placeholder="Pick an industry to tune" /></SelectTrigger>
                  <SelectContent>{industries.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
                </Select>
                {tunerIndustry && (
                  <div className="mt-4 space-y-2 max-h-80 overflow-y-auto pr-2" data-testid="if-edges">
                    {ifEdges.length === 0 && <p className="text-xs text-muted-foreground">No edges yet. Weights default to 1.0 in cascade queries.</p>}
                    {ifEdges.map((e) => (
                      <div key={e.function_id} className="flex items-center gap-3 text-sm">
                        <span className="flex-1 truncate">{functionsById[e.function_id]?.name || e.function_id}</span>
                        <Input type="number" step="0.1" min="0" max="5" defaultValue={e.weight} onBlur={(ev) => saveIfWeight(e.function_id, ev.target.value)} className="w-20 h-8" data-testid={`if-w-${e.function_id}`} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <p className="mt-4 text-xs text-muted-foreground">
              Weights use a 0.0–5.0 scale. Cascade queries sort results by (weight DESC, name ASC).
              Changes save on blur (click outside the input).
            </p>
          </Card>
        </TabsContent>

        {/* ------ Benchmarks (Pack 9) ------ */}
        <TabsContent value="benchmarks" className="mt-4">
          <BenchmarkScopeEditor />
        </TabsContent>
      </Tabs>
    </div>
  );
}
